import {
  Button,
  Divider,
  Form,
  Input,
  InputNumber,
  Space,
  Typography,
  notification,
} from '@pankod/refine-antd';
import { useEffect, useState } from 'react';
import { IPurchasedProductState } from 'reducers/product-purchase-reducer';
import { InventoryLotWithVariant, getInventoryLotsByVariant } from 'services/inventory';
import styles from './style.module.css';
import { useCan } from '@pankod/refine-core';

type EditProductFormProps = {
  editProduct: IPurchasedProductState;
  onSubmit: (editedProduct: IPurchasedProductState) => void;
};

const { Text } = Typography;

export const EditProductForm: React.FC<EditProductFormProps> = (props) => {
  const [inventoryLots, setInventoryLots] = useState<InventoryLotWithVariant[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [form] = Form.useForm();
  const { data: readPurchasePrice } = useCan({
    resource: 'purchase_price',
    action: 'read',
  });

  useEffect(() => {
    form.resetFields();
    setInventoryLots([]);

    (async () => {
      setIsLoading(true);
      try {
        if (!props.editProduct) return;

        let quantityString = '';
        const inventoryLotResponse = await getInventoryLotsByVariant(
          props.editProduct.productVariantId
        );
        setInventoryLots(inventoryLotResponse);

        form.setFieldsValue({
          variant: `${props.editProduct.sku} - ${props.editProduct.productVariantName} ${quantityString}`,
        });
      } catch (err: any) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
      setIsLoading(false);
    })();
  }, [props.editProduct, form]);

  const onFinish = (values: IPurchasedProductState) => {
    const newData = {
      ...props.editProduct,
      quantity: values.quantity,
      unitPrice: values.unitPrice,
    };

    props.onSubmit(newData);
    form.resetFields();
    setInventoryLots([]);
  };

  const availableStock = () => {
    return (
      <ul>
        {inventoryLots.map((lot) => (
          <li key={lot.id}>
            <Text>
              {lot.quantity} available at BDT {lot.unitPrice}
            </Text>
          </li>
        ))}
      </ul>
    );
  };

  const attributes: {
    attributeId: string;
    attributeName: string;
    attributeType: 'text' | 'float' | 'boolean' | 'dropdown';
    value: string;
  }[] = [];
  const templates = props.editProduct.productTemplates || [];
  templates.forEach((template) => {
    const { productTemplateAttributes } = template;
    attributes.push(...productTemplateAttributes);
  });

  const customFields = props.editProduct.customFields ?? [];

  const attributeElements = attributes.map((attribute) => (
    <div
      key={attribute.attributeId}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
    >
      <Typography.Text strong>{attribute.attributeName.toUpperCase()} : </Typography.Text>
      <Typography.Text>{attribute.value}</Typography.Text>
    </div>
  ));

  const customFieldElements = customFields.map((field, idx) => {
    const key = Object.keys(field)[0];
    const value = field[key];

    return (
      <div
        key={idx}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Typography.Text strong>{key.toUpperCase()} : </Typography.Text>
        <Typography.Text>{value}</Typography.Text>
      </div>
    );
  });

  return (
    <Form
      layout="vertical"
      initialValues={{
        variant: `${props.editProduct.sku} - ${props.editProduct.productVariantName}`,
        quantity: props.editProduct.quantity,
        unitPrice: props.editProduct.unitPrice,
      }}
      form={form}
      onFinish={onFinish}
    >
      <Form.Item label="Product" name="variant">
        <Input disabled />
      </Form.Item>

      <Divider />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography.Text strong>PARENT PRODUCT:</Typography.Text>
          <Typography.Text code>{props.editProduct.productName}</Typography.Text>
        </div>

        {attributeElements}
        {customFieldElements}

        {readPurchasePrice?.can && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography.Text strong>DEFAULT PURCHASE PRICE:</Typography.Text>
            <Typography.Text>{props.editProduct.purchasePrice}</Typography.Text>
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography.Text strong>DEFAULT SELLING PRICE:</Typography.Text>
          <Typography.Text>{props.editProduct.sellingPrice}</Typography.Text>
        </div>
      </div>
      <Divider />

      <Form.Item
        label="Latest Purchase Price"
        name="unitPrice"
        rules={[{ required: true }, { type: 'number', min: 0 }]}
      >
        <InputNumber style={{ width: '100%' }} precision={2} />
      </Form.Item>
      <Form.Item
        label="Quantity"
        name="quantity"
        rules={[
          {
            required: true,
            type: 'integer',
            min: 1,
            message: 'Quantity must be a positive integer value',
          },
        ]}
      >
        <InputNumber style={{ width: '100%' }} />
      </Form.Item>

      <Space direction="vertical">
        <Text className={styles['stock__text']}>* Available Stock</Text>
        {availableStock()}
      </Space>

      <Form.Item wrapperCol={{ span: 4, offset: 20 }}>
        <Button type="primary" htmlType="submit" loading={isLoading} disabled={isLoading}>
          Save
        </Button>
      </Form.Item>
    </Form>
  );
};
